import { addToast, closeAll } from '@heroui/react';
import { type ShowToastOptions, type ToastType } from './Toast.types';

// Matches the legacy ngToast lifespan (ngToastProvider.configure({ timeout: 2500 })) so migrated
// toasts stay on screen for the same duration. HeroUI's own default is 6000ms; callers can still
// override per-toast via the `timeout` option.
const DEFAULT_TIMEOUT_MS = 2500;

const TYPE_TO_COLOR: Record<ToastType, 'default' | 'success' | 'warning' | 'danger'> = {
    success: 'success',
    danger: 'danger',
    warning: 'warning',
    default: 'default',
};

// Our HeroUI theme only defines the base semantic color tokens (e.g. `text-success`), not the
// numeric scales (success-50/-100/-600) that HeroUI's default toast styling relies on. So we
// color just the status icon via the base tokens that resolve, and override the card's
// background/border/width below with component-library-aligned utilities.
const TYPE_TO_ICON_CLASS: Record<ToastType, string> = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    default: 'text-black/60',
};

// Styles the toast as a clean white card consistent with our component library (Modal/Popover):
// white background, subtle border + shadow, rounded corners, and a fixed, appropriately-sized
// width. HeroUI's structural defaults (radius, shadow, padding, spacing) are kept — its default
// `rounded-medium` computes to 12px, which is a design-system radius value (`rounded-7`).
function toastClassNames(type: ToastType) {
    return {
        // The region wrapper (motionDiv) is a full-width fixed element that HeroUI gives an inline
        // `pointer-events: all`, so its empty area intercepts clicks on whatever sits beneath it.
        // Force it click-through (important, to beat the inline style) and re-enable pointer events on
        // the card itself so the toast stays interactive.
        motionDiv: '!pointer-events-none',
        base: 'pointer-events-auto mx-auto w-[356px] max-w-[calc(100vw-2rem)] border-slate-grey-200 bg-white',
        // `content` is the flex row holding the icon + text; match its gap to the card's `p-3` (12px)
        // padding so the icon sits with equal spacing to the box edge and to the message.
        content: 'gap-x-3',
        title: 'text-sm font-semibold text-black',
        description: 'text-sm text-slate-grey',
        icon: TYPE_TO_ICON_CLASS[type],
    };
}

// The canonical way for React code to show a toast. Wraps HeroUI's imperative `addToast`
// (rendered by the app-wide <AppToastProvider/>) with our conventions. HeroUI pushes to a
// module-level global queue, so this is callable from anywhere — even outside React.
export function showToast({
    message,
    description,
    type = 'default',
    timeout = DEFAULT_TIMEOUT_MS,
}: ShowToastOptions): void {
    addToast({
        title: message,
        description,
        color: TYPE_TO_COLOR[type],
        timeout,
        classNames: toastClassNames(type),
    });
}

// Dismiss all currently visible toasts.
export function closeAllToasts(): void {
    closeAll();
}
